import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity,
  CheckCircle,
  XCircle,
  Server,
  Box,
  GitBranch,
} from 'lucide-react';
import { API, showError } from '../helpers';
import Card from './ui/Card';
import DashboardTrendCards from './DashboardTrendCards';
import AppErrorState from './AppErrorState';
import Loading from './Loading';
import { normalizeAppError } from '../helpers/appRuntime';

const colorPreset = {
  blue: { bg: 'var(--primary-50)', text: 'var(--primary-600)' },
  green: { bg: 'rgba(16, 185, 129, 0.12)', text: 'var(--success)' },
  red: { bg: 'rgba(239, 68, 68, 0.12)', text: 'var(--error)' },
  purple: { bg: '#f3e8ff', text: '#9333ea' },
  cyan: { bg: '#cffafe', text: '#0891b2' },
  orange: { bg: 'rgba(249, 115, 22, 0.12)', text: '#ea580c' },
};

const numberFormatter = new Intl.NumberFormat('zh-CN');

const formatNumber = (value) => numberFormatter.format(Number(value || 0));

const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => {
  const style = colorPreset[color] || colorPreset.blue;

  return (
    <Card padding='1rem' className='dashboard-stat-card'>
      <div className='dashboard-stat-top'>
        <div
          className='dashboard-stat-icon'
          style={{ backgroundColor: style.bg, color: style.text }}
        >
          <Icon size={18} />
        </div>
      </div>
      <div className='dashboard-stat-value'>{formatNumber(value)}</div>
      <div className='dashboard-stat-title'>{title}</div>
    </Card>
  );
};

const DashboardPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await API.get('/api/dashboard', {
        timeout: 10000,
      });
      const { success, data, message, error_kind: errorKind } = res.data || {};
      if (success) {
        setStats(data);
        setLoadError(null);
      } else {
        const nextError = normalizeAppError(
          new Error(message || '无法加载统计数据'),
          {
            stage: 'dashboard_data',
            path: '/',
            endpoint: '/api/dashboard',
            kind: errorKind,
          }
        );
        setStats(null);
        setLoadError(nextError);
        showError(nextError.appDisplayMessage);
      }
    } catch (error) {
      const nextError = normalizeAppError(error, {
        stage: 'dashboard_data',
        path: '/',
        endpoint: '/api/dashboard',
      });
      setStats(null);
      setLoadError(nextError);
      showError(nextError.appDisplayMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  if (loading) {
    return <Loading label='正在加载仪表盘数据...' minHeight='320px' />;
  }

  if (loadError || !stats) {
    return (
      <AppErrorState
        title='仪表盘加载失败'
        error={
          loadError ||
          normalizeAppError(new Error('仪表盘暂时不可用'), {
            stage: 'dashboard_data',
            path: '/',
            endpoint: '/api/dashboard',
          })
        }
        onRetry={() => {
          void loadStats();
        }}
        onReload={() => window.location.reload()}
      />
    );
  }

  return (
    <div className='dashboard-shell'>
      <div className='dashboard-stat-grid'>
        <StatCard
          title='总请求数(估算)'
          value={stats.total_requests}
          icon={Activity}
          color='blue'
        />
        <StatCard
          title='近7天成功'
          value={stats.success_requests}
          icon={CheckCircle}
          color='green'
        />
        <StatCard
          title='近7天失败'
          value={stats.failed_requests}
          icon={XCircle}
          color='red'
        />
        <StatCard
          title='活跃供应商'
          value={stats.total_providers}
          icon={Server}
          color='purple'
        />
        <StatCard
          title='可用模型'
          value={stats.total_models}
          icon={Box}
          color='cyan'
        />
        <StatCard
          title='路由条目'
          value={stats.total_routes}
          icon={GitBranch}
          color='orange'
        />
      </div>

      <DashboardTrendCards
        recentMetrics={stats.recent_metrics || []}
        recentRequests={stats.recent_requests || []}
        recentModelStats={stats.recent_model_stats || []}
      />
    </div>
  );
};

export default DashboardPanel;
